/**
 * SEATPILOT - FRONTEND CONFIGURATION
 * Uses REACT_APP_BACKEND_URL for preview/dev, auto-detects for production
 * Supports: Production (seatpilot.co.uk), Preview (emergentagent.com), Development (localhost)
 */

// Get backend URL - prioritizes env var for consistency
const getBackendUrl = () => {
  // Always use env var if available (this is set correctly per environment)
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  // For browser environment, do additional checks
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    
    // Production: seatpilot.co.uk - always use production URL
    if (hostname.includes('seatpilot.co.uk')) {
      return 'https://seatpilot.co.uk';
    }
    
    // Preview/Development on Emergent: use the env var (should be set to preview URL)
    if (hostname.includes('emergentagent.com') || hostname.includes('emergent.host')) {
      // Use env var which should point to the correct preview backend
      return envUrl || `${window.location.protocol}//${hostname}`;
    }
    
    // Local development - use localhost:8001
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8001';
    }
  }
  
  // Fallback: use env var or production URL
  return envUrl || 'https://seatpilot.co.uk';
};

// Get the base URL
const BACKEND_URL_VALUE = getBackendUrl();

export const BACKEND_URL = BACKEND_URL_VALUE;
export const getBaseUrl = () => {
  // Re-detect in case of dynamic changes (e.g., testing)
  if (typeof window !== 'undefined') {
    return getBackendUrl();
  }
  return BACKEND_URL_VALUE;
};
export const getApiUrl = () => `${getBaseUrl()}/api`;

// Page URLs - all use getBaseUrl() for dynamic detection
export const getWidgetUrl = (layoutId) => `${getBaseUrl()}/widget/${layoutId}`;
export const getDevDocsUrl = (section = null) => {
  const url = `${getBaseUrl()}/developer-docs`;
  return section ? `${url}?section=${section}` : url;
};
export const getDashboardUrl = (tab = null) => {
  const url = `${getBaseUrl()}/dashboard`;
  return tab ? `${url}?tab=${tab}` : url;
};
export const getLoginUrl = () => `${getBaseUrl()}/login`;
export const getResetPasswordUrl = (token) => `${getBaseUrl()}/reset-password?token=${token}`;
export const getApiDocsUrl = () => `${getBaseUrl()}/api/docs`;

// Branding
export const BRAND_NAME = "SeatPilot";
export const SUPPORT_EMAIL = "support@seatpilot.co.uk";
export const CONTACT_EMAIL = "hello@seatpilot.co.uk";
export const BILLING_EMAIL = "billing@seatpilot.co.uk";
export const PRODUCTION_URL = "https://seatpilot.co.uk";

export const getSupportEmail = () => SUPPORT_EMAIL;
export const getContactEmail = () => CONTACT_EMAIL;
export const getBillingEmail = () => BILLING_EMAIL;

// Environment detection - based on current location
export const isProduction = () => {
  if (typeof window !== 'undefined') {
    return window.location.hostname.includes('seatpilot.co.uk');
  }
  return BACKEND_URL_VALUE.includes('seatpilot.co.uk');
};
export const isPreview = () => {
  if (typeof window !== 'undefined') {
    return window.location.hostname.includes('emergentagent.com') || 
           window.location.hostname.includes('emergent.host');
  }
  return BACKEND_URL_VALUE.includes('emergentagent.com') || BACKEND_URL_VALUE.includes('emergent.host');
};
export const isDevelopment = () => {
  if (typeof window !== 'undefined') {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }
  return false;
};
export const getEnvironment = () => {
  if (isProduction()) return "production";
  if (isPreview()) return "preview";
  if (isDevelopment()) return "development";
  return "unknown";
};

const config = {
  BACKEND_URL,
  getBaseUrl,
  getApiUrl,
  getWidgetUrl,
  getDevDocsUrl,
  getDashboardUrl,
  getLoginUrl,
  getResetPasswordUrl,
  getApiDocsUrl,
  BRAND_NAME,
  SUPPORT_EMAIL,
  CONTACT_EMAIL,
  BILLING_EMAIL,
  getSupportEmail,
  getContactEmail,
  getBillingEmail,
  isProduction,
  isPreview,
  isDevelopment,
  getEnvironment,
};

export default config;
